/**
 * Zuordnungsvorschläge für Zahlungseingänge aus dem Kontoauszug. Rein: Eingang und offene Rechnungen rein, bewertete
 * Vorschläge mit Begründung raus. Gebucht wird nie automatisch; die Agentur bestätigt je Eingang, die Datenbank bucht
 * über invoice_record_payment.
 *
 * Signale: Rechnungsnummer im Verwendungszweck (normalisiert, auch ohne Bindestriche oder Präfix), strukturierte
 * Referenz (RF nach ISO 11649, QR-Referenz CH), Betrag gleich offenem Betrag, bekanntes Auftraggeberkonto (Prüfwert der
 * IBAN), Name des Auftraggebers gegen Marke bzw. Creator, Summe mehrerer offener Rechnungen einer Marke.
 */
import { foldText } from "./countries.js";
import { normalizeInvoiceNumber } from "./invoice-number.js";
import { findRfReference, qrReferencePayload, rfPayload } from "./bank-statement.js";
import type { Cents, IsoDate } from "./types.js";

export type MatchInvoiceKind = "agency_to_brand" | "agency_to_talent" | "talent_to_brand" | "talent_to_agency";

export interface MatchInvoice {
  id: string;
  number: string | null;
  kind: MatchInvoiceKind;
  status: string;
  currency: string;
  grossCents: Cents;
  paidCents: Cents;
  brandId: string | null;
  talentId: string | null;
  dueDate: IsoDate | null;
  issueDate: IsoDate | null;
  /** Stornorechnung zu (wird nie zugeordnet) */
  creditFor?: string | null;
  /** Beim Festschreiben eingefrorene Zahlungsreferenz (QR-Referenz bzw. Creditor Reference, 0069), wie sie auf dem Beleg steht */
  paymentRef?: string | null;
}

/** Marke oder Creator als möglicher Zahler: Namen (Anzeige- und Firmenname) und Prüfwerte bekannter Konten. */
export interface MatchParty {
  id: string;
  kind: "brand" | "talent";
  names: string[];
  ibanHashes: string[];
}

export interface MatchLine {
  direction: "credit" | "debit";
  amountCents: Cents;
  currency: string;
  counterpartyName: string | null;
  counterpartyIbanHash: string | null;
  remittance: string | null;
  creditorRef: string | null;
}

export type ReasonCode =
  | "number" | "number_short" | "creditor_ref" | "amount_exact" | "amount_sum" | "iban_known" | "name" | "name_partial"
  | "partial_rest" | "overpaid" | "sum_short" | "creator_direct" | "already_paid" | "via_creator" | "amount_only"
  | "same_amount_several" | "oldest_first";

export interface MatchReason {
  code: ReasonCode;
  numbers?: string[];
  cents?: Cents;
  party?: string;
}

export type SuggestionKind = "exact" | "combination" | "partial" | "overpayment" | "creator_direct" | "already_paid" | "via_creator" | "amount_only";
export type SuggestionLevel = "sicher" | "wahrscheinlich" | "möglich" | "hinweis";

export interface Suggestion {
  kind: SuggestionKind;
  /** 0–100 */
  score: number;
  level: SuggestionLevel;
  /** Buchungsvorschlag je Rechnung; leer bei Hinweisen (Direktweg, schon bezahlt, Marke zahlte an den Creator) */
  allocations: { invoiceId: string; amountCents: Cents }[];
  /** Genannte Rechnungen (auch bei Hinweisen) */
  invoiceIds: string[];
  partyId: string | null;
  reasons: MatchReason[];
  /** Nach der Buchung offen (Teilzahlung, positiv) bzw. Mehrbetrag (Überzahlung, negativ); 0 = glatt */
  restCents: Cents;
}

export const openCents = (i: Pick<MatchInvoice, "grossCents" | "paidCents">): Cents => i.grossCents - i.paidCents;

/** Rechnungen, auf die ein Eingang gebucht werden kann: festgeschriebene, offene Rechnungen der Agentur (keine Storno). */
export function isBookable(i: MatchInvoice): boolean {
  return (i.kind === "agency_to_brand" || i.kind === "agency_to_talent") && (i.status === "approved" || i.status === "sent")
    && !i.creditFor && openCents(i) > 0;
}

// ── Namen ─────────────────────────────────────────────────────────────────────

const LEGAL_FORMS = new Set(["gmbh", "ag", "kg", "ohg", "og", "ug", "haftungsbeschrankt", "mbh", "co", "e", "k", "eu", "ek", "ev", "gbr", "se", "sa",
  "sarl", "sagl", "ltd", "limited", "llc", "inc", "bv", "nv", "kft", "srl", "spa", "gesmbh", "kommanditgesellschaft", "und", "and", "the", "der", "die", "das"]);

/** Wörter eines Namens ohne Rechtsform und Füllwörter („Lunava Cosmetics GmbH & Co. KG“ → lunava, cosmetics). */
export function nameTokens(name: string | null | undefined): string[] {
  return foldText(name ?? "").replace(/[^0-9a-z]+/g, " ").split(" ").filter((t) => t.length >= 2 && !LEGAL_FORMS.has(t));
}

/**
 * Ähnlichkeit zweier Namen 0–1: Anteil der Wörter des kürzeren Namens, die im längeren vorkommen (Reihenfolge egal,
 * „LINDEN, MARA“ = „Mara Linden“); zusammengeschrieben gleich („Sol Viq“ = „Solviq“) zählt voll.
 */
export function nameSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const x = nameTokens(a), y = nameTokens(b);
  if (!x.length || !y.length) return 0;
  if (x.join("") === y.join("") || [...x].sort().join("") === [...y].sort().join("")) return 1;
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  const longSet = new Set(long);
  const longJoined = long.join("");
  const hits = short.filter((t) => longSet.has(t) || (t.length >= 5 && longJoined.includes(t))).length;
  return hits / short.length;
}

// ── Rechnungsnummer im Text ──────────────────────────────────────────────────

/** Segmente einer Nummer ohne führende Nullen („TK-2026-039“ → TK, 2026, 39). */
const segments = (s: string): string[] => normalizeInvoiceNumber(s).split("-").filter(Boolean);

/**
 * Kommt die Rechnungsnummer im Text vor? „full“: mit Präfix, egal ob mit Bindestrich, Leerzeichen, zusammengeschrieben
 * oder mit führenden Nullen („TK 2026 39“, „TK2026039“); „short“: ohne den Buchstaben-Präfix, aber nur, wenn der Rest
 * genau so geschrieben ist wie auf der Rechnung (samt führender Nullen, „2026-039“ oder „2026039“) und mindestens fünf
 * signifikante Ziffern hat. So trifft „RE-00042“ nicht auf „Bestellung 42“ und „NT-2026-09-001“ nicht auf „2026 09 1“.
 * Nummern ganz ohne Buchstaben-Präfix zählen nur, wenn sie genau so im Text stehen; sonst null.
 */
export function findNumberInText(number: string | null | undefined, text: string): "full" | "short" | null {
  if (!number) return null;
  const segs = segments(number);
  const raw = number.toUpperCase().split(/[^0-9A-Z]+/).filter(Boolean);
  if (!segs.length || !raw.length) return null;
  const compact = raw.join("");
  const target = segs.join("-");
  // Präfix: führende reine Buchstabensegmente („RE“, „TK“)
  let k = 0;
  while (k < raw.length - 1 && /^[A-Z]+$/.test(raw[k]!)) k++;
  const hasPrefix = k > 0;
  const restCompact = raw.slice(k).join("");
  // Mindestlänge an den signifikanten Ziffern (ohne führende Nullen), nicht an den aufgefüllten
  const restAllowed = restCompact.replace(/^0+/, "").replace(/[^0-9]/g, "").length >= 5;
  const tokens = text.toUpperCase().split(/[^0-9A-Z]+/).filter(Boolean);
  const normTokens = tokens.map((t) => segments(t).join("-"));
  let short = false;
  for (let i = 0; i < tokens.length; i++) {
    let joined = "";
    const parts: string[] = [];
    for (let j = i; j < Math.min(tokens.length, i + raw.length + 1); j++) {
      joined += tokens[j];
      parts.push(normTokens[j]!);
      if (hasPrefix && (joined === compact || parts.join("-") === target)) return "full";
      if (restAllowed && joined === restCompact) {
        if (!hasPrefix) return "full";
        short = true;
      }
    }
  }
  return short ? "short" : null;
}

const refPattern = (ref: string) => [...ref].map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s*");

/**
 * Steht die eingefrorene QR-Referenz im Text? Leerzeichen innerhalb der Referenz sind erlaubt (Fünferblöcke der Bank),
 * direkt danach darf aber kein Buchstabe und keine Ziffer folgen. QR-Referenzen haben immer 27 Ziffern, eine kürzere kann
 * also nicht Anfang einer längeren sein. RF-Referenzen vergleicht rfReferencesIn auf Gleichheit (GB-D1).
 */
function containsRef(text: string, frozen: string): boolean {
  return new RegExp(`${refPattern(frozen)}(?![0-9A-Za-z])`, "i").test(text);
}

/**
 * Alle gültigen RF-Referenzen (ISO 11649) im Text, so gelesen wie findRfReference (Viererblöcke, Ende an einer kürzeren
 * Gruppe, vor einem Wort oder an Satzzeichen), und der Text ohne sie. Gegenprüfung GB-D1: „RF19 RE20 2611 5“ ist genau
 * RF19RE2026115; ein Teiltreffer wie RF19RE202611 (andere Rechnung mit gleicher Prüfziffer, endet an einer Blockgrenze)
 * zählt nicht, und die Nummernsuche sieht die Blöcke der Referenz nicht mehr („RE20 2611“ ist nicht RE-2026-11).
 */
function rfReferencesIn(text: string): { refs: string[]; rest: string } {
  const refs: string[] = [];
  let rest = text;
  for (let i = 0; i < 20; i++) {
    const ref = findRfReference(rest);
    if (!ref) break;
    const m = new RegExp(`(?<![0-9A-Za-z])${refPattern(ref)}`, "i").exec(rest);
    if (!m) break;
    refs.push(ref);
    rest = `${rest.slice(0, m.index)} ${rest.slice(m.index + m[0].length)}`;
  }
  return { refs, rest };
}

/** Findet Rechnungen, deren Nummer im Text oder in der strukturierten Referenz steht. */
function numberHits(line: MatchLine, invoices: readonly MatchInvoice[]): Map<string, "full" | "short" | "ref"> {
  const text = `${line.remittance ?? ""} ${line.creditorRef ?? ""}`;
  const rf = rfPayload(line.creditorRef);
  const qr = qrReferencePayload(line.creditorRef);
  const hits = new Map<string, "full" | "short" | "ref">();
  const ownRef = (line.creditorRef ?? "").replace(/\s+/g, "").toUpperCase();
  // GB-D1: RF-Referenzen im Text genau bestimmen (nicht als Teiltreffer) und aus der Nummernsuche nehmen
  const { refs: textRefs, rest } = rfReferencesIn(text);
  const textPayloads = textRefs.map((r) => rfPayload(r)).filter((p): p is string => !!p);
  const samePayload = (p: string, compact: string) => p === compact || segments(p).join("-") === segments(compact).join("-");
  for (const inv of invoices) {
    // Eingefrorene Referenz vom Beleg (mit Präfix der Bank oder Zählziffer): genau diese Zeichenkette zählt. RF-Referenzen
    // nur auf Gleichheit mit einer im Text gefundenen Referenz (RF25RE2026104 ist nicht RF25RE20261047, auch nicht in
    // Viererblöcken „RF25 RE20 2610 47“), QR-Referenzen (27 Ziffern) als ganze Zeichenkette
    const frozen = (inv.paymentRef ?? "").replace(/\s+/g, "").toUpperCase();
    const frozenIsRf = /^RF\d{2}/.test(frozen) && rfPayload(frozen) !== null;
    if (frozen && (ownRef === frozen || (frozenIsRf ? textRefs.includes(frozen) : containsRef(text, frozen)))) {
      hits.set(inv.id, "ref");
      continue;
    }
    if (!inv.number) continue;
    const compact = inv.number.toUpperCase().replace(/[^0-9A-Z]/g, "");
    if ((rf && samePayload(rf, compact)) || textPayloads.some((p) => samePayload(p, compact))) {
      hits.set(inv.id, "ref");
      continue;
    }
    const digits = compact.replace(/[^0-9]/g, "").replace(/^0+/, "");
    if (qr && digits.length >= 4 && qr === digits) {
      hits.set(inv.id, "ref");
      continue;
    }
    const f = findNumberInText(inv.number, rest);
    if (f) hits.set(inv.id, f);
  }
  // Nummer ohne Präfix passt zu mehreren Rechnungen (z. B. RE-2026-039 und GS-2026-039): zu unsicher, weglassen
  const shortKeys = new Map<string, string[]>();
  for (const inv of invoices) {
    if (hits.get(inv.id) !== "short" || !inv.number) continue;
    const key = segments(inv.number).filter((x, n, all) => n > 0 || !/^[A-Z]+$/.test(x) || all.length === 1).join("-");
    shortKeys.set(key, [...(shortKeys.get(key) ?? []), inv.id]);
  }
  for (const ids of shortKeys.values()) if (ids.length > 1) for (const id of ids) hits.delete(id);
  return hits;
}

// ── Summen ───────────────────────────────────────────────────────────────────

/**
 * Teilmengen offener Rechnungen, deren offene Beträge genau den Betrag ergeben (mindestens zwei Rechnungen). Ältere
 * Fälligkeiten und weniger Rechnungen zuerst; höchstens `limit` Treffer. Durchsucht die 16 ältesten Rechnungen.
 */
export function findCombinations(amountCents: Cents, invoices: readonly MatchInvoice[], limit = 3, mustInclude: readonly string[] = []): MatchInvoice[][] {
  const pool = [...invoices].filter((i) => openCents(i) > 0).sort(byDue).slice(0, 16);
  const out: MatchInvoice[][] = [];
  const n = pool.length;
  const pick: MatchInvoice[] = [];
  const must = new Set(mustInclude);
  const walk = (start: number, sum: number) => {
    if (out.length >= 50) return;
    if (sum === amountCents && pick.length >= 2 && [...must].every((id) => pick.some((p) => p.id === id))) {
      out.push([...pick]);
      return;
    }
    if (sum >= amountCents || pick.length >= 6) return;
    for (let i = start; i < n; i++) {
      pick.push(pool[i]!);
      walk(i + 1, sum + openCents(pool[i]!));
      pick.pop();
    }
  };
  walk(0, 0);
  const age = (set: MatchInvoice[]) => set.reduce((s, i) => s + pool.indexOf(i), 0);
  return out.sort((a, b) => a.length - b.length || age(a) - age(b)).slice(0, limit);
}

function byDue(a: MatchInvoice, b: MatchInvoice): number {
  const x = a.dueDate ?? a.issueDate ?? "9999-12-31", y = b.dueDate ?? b.issueDate ?? "9999-12-31";
  return x < y ? -1 : x > y ? 1 : (a.number ?? "").localeCompare(b.number ?? "", "de", { numeric: true });
}

// ── Vorschläge ───────────────────────────────────────────────────────────────

export function levelOf(score: number): SuggestionLevel {
  return score >= 90 ? "sicher" : score >= 65 ? "wahrscheinlich" : "möglich";
}

/** Wer hat gezahlt? Bekanntes Konto (sicher) oder Name (ähnlich). */
export function identifyParties(line: MatchLine, parties: readonly MatchParty[]): { party: MatchParty; by: "iban" | "name" | "name_partial"; score: number }[] {
  const out: { party: MatchParty; by: "iban" | "name" | "name_partial"; score: number }[] = [];
  for (const p of parties) {
    if (line.counterpartyIbanHash && p.ibanHashes.includes(line.counterpartyIbanHash)) {
      out.push({ party: p, by: "iban", score: 1 });
      continue;
    }
    const sim = Math.max(0, ...p.names.map((n) => nameSimilarity(line.counterpartyName, n)));
    if (sim >= 0.99) out.push({ party: p, by: "name", score: sim });
    else if (sim >= 0.5) out.push({ party: p, by: "name_partial", score: sim });
  }
  return out.sort((a, b) => (a.by === "iban" ? 0 : 1) - (b.by === "iban" ? 0 : 1) || b.score - a.score);
}

const partyInvoices = (p: MatchParty, invoices: readonly MatchInvoice[]) =>
  invoices.filter((i) => (p.kind === "brand" ? i.kind === "agency_to_brand" && i.brandId === p.id : i.kind === "agency_to_talent" && i.talentId === p.id));

/**
 * Vorschläge für einen Eingang, bestbewertet zuerst (höchstens `max`). Nur Haben-Buchungen; Soll-Buchungen und
 * Rückbuchungen liefern nichts (nur Anzeige). Währungen werden nie gemischt.
 */
export function suggestMatches(line: MatchLine, ctx: { invoices: readonly MatchInvoice[]; parties: readonly MatchParty[] }, opts: { max?: number } = {}): Suggestion[] {
  if (line.direction !== "credit" || line.amountCents <= 0) return [];
  const amount = line.amountCents;
  const sameCur = ctx.invoices.filter((i) => i.currency === line.currency);
  const bookable = sameCur.filter(isBookable).sort(byDue);
  const hits = numberHits(line, sameCur);
  const found = identifyParties(line, ctx.parties);
  const out: Suggestion[] = [];
  const nr = (i: MatchInvoice) => i.number ?? "ohne Nummer";
  const add = (s: Omit<Suggestion, "level"> & { level?: SuggestionLevel }) => out.push({ ...s, score: Math.min(100, Math.round(s.score)), level: s.level ?? levelOf(s.score) });
  const partyReason = (partyId: string | null): MatchReason[] => {
    const f = found.find((x) => x.party.id === partyId);
    if (!f) return [];
    return [{ code: f.by === "iban" ? "iban_known" : f.by === "name" ? "name" : "name_partial", party: f.party.names[0] ?? "" }];
  };
  const partyBonus = (partyId: string | null) => {
    const f = found.find((x) => x.party.id === partyId);
    return !f ? 0 : f.by === "iban" ? 8 : f.by === "name" ? 5 : 2;
  };
  const partyOf = (i: MatchInvoice): string | null => (i.kind === "agency_to_talent" || i.kind === "talent_to_brand" && !i.brandId ? i.talentId : i.brandId);

  // 1  Nummer (oder Referenz) im Verwendungszweck
  const numbered = bookable.filter((i) => hits.has(i.id));
  if (numbered.length) {
    const strength = (i: MatchInvoice) => (hits.get(i.id) === "short" ? "number_short" : hits.get(i.id) === "ref" ? "creditor_ref" : "number") as ReasonCode;
    const base = numbered.some((i) => hits.get(i.id) !== "short") ? 0 : -6;
    const sum = numbered.reduce((s, i) => s + openCents(i), 0);
    const pid = numbered.every((i) => partyOf(i) === partyOf(numbered[0]!)) ? partyOf(numbered[0]!) : null;
    const numReasons: MatchReason[] = numbered.map((i) => ({ code: strength(i), numbers: [nr(i)] }));
    // Nur „ohne Präfix“ getroffen und weder Betrag noch Zahler bestätigen es: höchstens „möglich“ (nie mit einem Klick)
    const unconfirmed = base < 0 && partyBonus(pid) === 0;
    const capped = (score: number) => (unconfirmed ? Math.min(score, 60) : score);
    if (sum === amount) {
      add({ kind: numbered.length > 1 ? "combination" : "exact", score: 97 + base + partyBonus(pid), allocations: numbered.map((i) => ({ invoiceId: i.id, amountCents: openCents(i) })),
        invoiceIds: numbered.map((i) => i.id), partyId: pid, restCents: 0,
        reasons: [...numReasons, { code: numbered.length > 1 ? "amount_sum" : "amount_exact", numbers: numbered.map(nr), cents: amount }, ...partyReason(pid)] });
    } else if (sum > amount) {
      // Reicht nicht für alle: in Reihenfolge der Fälligkeit, die letzte als Teilzahlung
      let left = amount;
      const allocations: { invoiceId: string; amountCents: Cents }[] = [];
      for (const i of numbered) {
        if (left <= 0) break;
        const a = Math.min(left, openCents(i));
        allocations.push({ invoiceId: i.id, amountCents: a });
        left -= a;
      }
      add({ kind: "partial", score: capped(80 + base + partyBonus(pid)), allocations, invoiceIds: numbered.map((i) => i.id), partyId: pid, restCents: sum - amount,
        reasons: [...numReasons, numbered.length > 1 ? { code: "sum_short", cents: sum - amount } : { code: "partial_rest", cents: sum - amount }, ...partyReason(pid)] });
    } else {
      // Mehr als die genannten Rechnungen: weitere offene Rechnungen derselben Marke, die den Rest genau ergeben?
      const others = pid ? bookable.filter((i) => !hits.has(i.id) && partyOf(i) === pid) : [];
      const combo = findCombinations(amount - sum, others, 1)[0] ?? others.filter((i) => openCents(i) === amount - sum).slice(0, 1);
      if (combo.length && combo.reduce((s, i) => s + openCents(i), 0) === amount - sum) {
        const all = [...numbered, ...combo];
        add({ kind: "combination", score: 88 + base + partyBonus(pid), allocations: all.map((i) => ({ invoiceId: i.id, amountCents: openCents(i) })),
          invoiceIds: all.map((i) => i.id), partyId: pid, restCents: 0,
          reasons: [...numReasons, { code: "amount_sum", numbers: all.map(nr), cents: amount }, ...partyReason(pid)] });
      }
      const last = numbered[numbered.length - 1]!;
      // Überzahlung nie mit einem Klick: Tippfehler der Marke (Faktor 10) erst im Aufteilen-Dialog mit Hinweis prüfen
      add({ kind: "overpayment", score: Math.min(64, capped(76 + base + partyBonus(pid))), level: "möglich",
        allocations: numbered.map((i) => ({ invoiceId: i.id, amountCents: i === last ? openCents(i) + (amount - sum) : openCents(i) })),
        invoiceIds: numbered.map((i) => i.id), partyId: pid, restCents: -(amount - sum),
        reasons: [...numReasons, { code: "overpaid", cents: amount - sum, numbers: [nr(last)] }, ...partyReason(pid)] });
    }
  }

  // 2  Zahler erkannt (Konto oder Name): Betrag gleich einer offenen Rechnung, Summe mehrerer, sonst Teilzahlung
  for (const f of found) {
    const own = partyInvoices(f.party, bookable).filter((i) => !numbered.includes(i));
    if (!own.length) continue;
    const bonus = f.by === "iban" ? 12 : f.by === "name" ? 5 : -10;
    const pr = partyReason(f.party.id);
    const exact = own.filter((i) => openCents(i) === amount);
    for (const i of exact.slice(0, 2)) {
      add({ kind: "exact", score: 78 + bonus - (exact.length > 1 ? 10 : 0), allocations: [{ invoiceId: i.id, amountCents: amount }], invoiceIds: [i.id], partyId: f.party.id, restCents: 0,
        reasons: [{ code: "amount_exact", numbers: [nr(i)], cents: amount }, ...pr, ...(exact.length > 1 ? [{ code: "same_amount_several" as const, numbers: exact.map(nr) }] : [])] });
    }
    for (const combo of findCombinations(amount, own, 2)) {
      add({ kind: "combination", score: 78 + bonus - (combo.length - 2) * 3, allocations: combo.map((i) => ({ invoiceId: i.id, amountCents: openCents(i) })),
        invoiceIds: combo.map((i) => i.id), partyId: f.party.id, restCents: 0, reasons: [{ code: "amount_sum", numbers: combo.map(nr), cents: amount }, ...pr] });
    }
    if (!exact.length && !numbered.length) {
      const oldest = own[0]!;
      const open = openCents(oldest);
      if (open > amount) {
        add({ kind: "partial", score: 45 + bonus, allocations: [{ invoiceId: oldest.id, amountCents: amount }], invoiceIds: [oldest.id], partyId: f.party.id, restCents: open - amount,
          reasons: [...pr, { code: "oldest_first", numbers: [nr(oldest)] }, { code: "partial_rest", cents: open - amount }] });
      } else if (own.length === 1) {
        add({ kind: "overpayment", score: Math.min(64, 40 + bonus), level: "möglich", allocations: [{ invoiceId: oldest.id, amountCents: amount }], invoiceIds: [oldest.id], partyId: f.party.id, restCents: open - amount,
          reasons: [...pr, { code: "overpaid", cents: amount - open, numbers: [nr(oldest)] }] });
      }
    }
  }

  // 3  Nur der Betrag passt (Zahler unbekannt)
  if (!numbered.length && !found.length) {
    const same = bookable.filter((i) => openCents(i) === amount);
    for (const i of same.slice(0, 3)) {
      add({ kind: "amount_only", score: same.length === 1 ? 52 : 35, allocations: [{ invoiceId: i.id, amountCents: amount }], invoiceIds: [i.id], partyId: partyOf(i), restCents: 0,
        reasons: [{ code: "amount_only", numbers: [nr(i)], cents: amount }, ...(same.length > 1 ? [{ code: "same_amount_several" as const, numbers: same.map(nr) }] : [])] });
    }
  }

  // 4  Hinweise: Creator-Rechnung im Direktweg, schon bezahlt, Marke zahlte an den Creator
  for (const i of sameCur) {
    if (!hits.has(i.id) || isBookable(i)) continue;
    if (i.kind === "talent_to_brand" && !["cancelled", "discarded", "withdrawn", "rejected", "draft"].includes(i.status)) {
      add({ kind: "creator_direct", score: 70, level: "hinweis", allocations: [], invoiceIds: [i.id], partyId: i.brandId, restCents: 0,
        reasons: [{ code: "creator_direct", numbers: [nr(i)], cents: amount }] });
    } else if ((i.kind === "agency_to_brand" || i.kind === "agency_to_talent") && i.status === "paid" && !i.creditFor) {
      add({ kind: "already_paid", score: 60, level: "hinweis", allocations: [], invoiceIds: [i.id], partyId: partyOf(i), restCents: 0,
        reasons: [{ code: "already_paid", numbers: [nr(i)], cents: amount }] });
    }
  }
  const creator = found.find((f) => f.party.kind === "talent" && f.by !== "name_partial");
  if (creator && !out.some((s) => s.allocations.length && s.partyId === creator.party.id)) {
    const brandInvoices = bookable.filter((i) => i.kind === "agency_to_brand" && i.talentId === creator.party.id).slice(0, 3);
    if (brandInvoices.length) {
      add({ kind: "via_creator", score: 50, level: "hinweis", allocations: [], invoiceIds: brandInvoices.map((i) => i.id), partyId: creator.party.id, restCents: 0,
        reasons: [{ code: "via_creator", numbers: brandInvoices.map(nr), party: creator.party.names[0] ?? "" }] });
    }
  }

  // Doppelte Vorschläge (gleiche Aufteilung) zusammenfassen, bestbewertet zuerst
  const seen = new Set<string>();
  return out
    .sort((a, b) => b.score - a.score || (a.level === "hinweis" ? 1 : 0) - (b.level === "hinweis" ? 1 : 0))
    .filter((s) => {
      const key = `${s.kind === "creator_direct" || s.kind === "already_paid" || s.kind === "via_creator" ? s.kind : "book"}|${s.allocations.map((a) => `${a.invoiceId}:${a.amountCents}`).sort().join(",")}|${s.allocations.length ? "" : s.invoiceIds.join(",")}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, opts.max ?? 5);
}

/** Begründung als Satz (Agentur-Portal, Deutsch); `money` formatiert Cent in der Währung des Eingangs. */
export function describeReason(r: MatchReason, money: (cents: Cents) => string): string {
  const list = (r.numbers ?? []).join(" + ");
  switch (r.code) {
    case "number": return `Rechnungsnummer ${list} im Verwendungszweck`;
    case "number_short": return `Rechnungsnummer ${list} im Verwendungszweck (ohne Präfix)`;
    case "creditor_ref": return `Strukturierte Referenz passt zu ${list}`;
    case "amount_exact": return `Betrag entspricht dem offenen Betrag von ${list}`;
    case "amount_sum": return `Betrag = Summe der offenen Beträge von ${list}`;
    case "iban_known": return `Konto des Auftraggebers ist bei ${r.party} bekannt`;
    case "name": return `Auftraggeber passt zu ${r.party}`;
    case "name_partial": return `Auftraggeber ähnelt ${r.party}`;
    case "partial_rest": return `Teilzahlung: ${money(r.cents ?? 0)} bleiben offen`;
    case "sum_short": return `Reicht nicht für alle genannten Rechnungen: ${money(r.cents ?? 0)} bleiben offen`;
    case "overpaid": return `Überzahlung um ${money(r.cents ?? 0)}: steht danach als Guthaben an ${list}`;
    case "oldest_first": return `Älteste offene Rechnung zuerst (${list})`;
    case "amount_only": return `Nur der Betrag passt (${list}); Auftraggeber und Verwendungszweck nennen die Rechnung nicht`;
    case "same_amount_several": return `Mehrere offene Rechnungen über denselben Betrag: ${(r.numbers ?? []).join(", ")}`;
    case "creator_direct": return `Creator-Rechnung ${list} im Direktweg: Die Marke hätte an den Creator zahlen sollen. Geld an den Creator weiterleiten und die Zahlung in der Creator-Rechnung bestätigen; diesen Eingang danach mit Grund ignorieren.`;
    case "already_paid": return `Rechnung ${list} ist bereits bezahlt: mögliche Doppelzahlung; Erstattung klären und den Eingang danach mit Grund ignorieren.`;
    case "via_creator": return `Zahlung kommt von ${r.party} (Creator). Hat die Marke an den Creator gezahlt? Dann an der Rechnung ${list} „Marke zahlte an den Creator“ vermerken und den Anteil der Agentur mit einer Rechnung an den Creator nachfordern.`;
  }
}

// ── Aufteilung prüfen ────────────────────────────────────────────────────────

export interface AllocationDraft {
  invoiceId: string;
  amountCents: Cents;
  /** Rest dieser Rechnung ausbuchen (Skonto, Bankgebühr, Kursdifferenz) statt als Teilzahlung offen zu lassen */
  writeOffRest?: boolean;
  writeOffReason?: string | null;
}

export interface AllocationCheck {
  ok: boolean;
  problems: string[];
  /** Nicht zugeordneter Rest des Eingangs (braucht eine Notiz) */
  restCents: Cents;
  /** Rechnungen, die danach überzahlt sind (Mehrbetrag je Rechnung) */
  overpaid: { invoiceId: string; cents: Cents }[];
  /** Rechnungen, die danach teilweise offen bleiben */
  partial: { invoiceId: string; cents: Cents }[];
}

/** Prüft eine Aufteilung vor dem Buchen (die Datenbank prüft dasselbe noch einmal). */
export function checkAllocation(line: Pick<MatchLine, "amountCents" | "currency">, drafts: readonly AllocationDraft[], invoices: readonly MatchInvoice[], note?: string | null): AllocationCheck {
  const problems: string[] = [];
  const byId = new Map(invoices.map((i) => [i.id, i]));
  const used = drafts.filter((d) => d.amountCents !== 0 || d.invoiceId);
  if (!used.length) problems.push("Bitte mindestens eine Rechnung wählen.");
  if (used.length > 20) problems.push("Höchstens 20 Rechnungen je Eingang.");
  const ids = new Set<string>();
  const overpaid: { invoiceId: string; cents: Cents }[] = [];
  const partial: { invoiceId: string; cents: Cents }[] = [];
  const writtenOff: string[] = [];
  let sum = 0;
  for (const d of used) {
    const inv = byId.get(d.invoiceId);
    if (!inv) {
      problems.push("Eine gewählte Rechnung ist nicht (mehr) offen.");
      continue;
    }
    if (ids.has(d.invoiceId)) problems.push(`Rechnung ${inv.number ?? ""} ist doppelt gewählt.`);
    ids.add(d.invoiceId);
    if (!isBookable(inv)) problems.push(`Rechnung ${inv.number ?? ""} ist nicht offen oder keine Rechnung der Agentur.`);
    if (inv.currency !== line.currency) problems.push(`Rechnung ${inv.number ?? ""} ist in ${inv.currency}, der Eingang in ${line.currency}.`);
    if (!Number.isSafeInteger(d.amountCents) || d.amountCents <= 0) {
      problems.push(`Bitte für ${inv.number ?? "die Rechnung"} einen Betrag größer 0 angeben.`);
      continue;
    }
    sum += d.amountCents;
    const open = openCents(inv);
    if (d.amountCents > open) overpaid.push({ invoiceId: inv.id, cents: d.amountCents - open });
    else if (d.amountCents < open) {
      if (d.writeOffRest && (d.writeOffReason ?? "").trim().length < 3) problems.push(`Bitte den Grund für die ausgebuchte Differenz bei ${inv.number ?? "der Rechnung"} angeben.`);
      if (!d.writeOffRest) partial.push({ invoiceId: inv.id, cents: open - d.amountCents });
      else writtenOff.push(inv.number ?? "der Rechnung");
    }
  }
  if (sum > line.amountCents) problems.push("Die Aufteilung ist höher als der Eingang.");
  const rest = line.amountCents - sum;
  if (rest > 0 && writtenOff.length) {
    // Eingegangenes Geld würde als Verlust ausgebucht und zugleich als Rest ohne Rechnung vermerkt
    problems.push(`Es bleibt ein Rest ohne Rechnung, und bei ${writtenOff.join(", ")} wird zugleich eine Differenz ausgebucht. Bitte den Rest erst auf diese Rechnung legen; ausbuchen nur, was wirklich fehlt.`);
  } else if (rest > 0 && used.length && (note ?? "").trim().length < 3) problems.push("Ein Rest bleibt ohne Rechnung: bitte in der Notiz sagen, wofür er ist.");
  return { ok: problems.length === 0, problems, restCents: Math.max(0, rest), overpaid, partial };
}
